import { Component, Input, OnInit, OnChanges, SimpleChanges, Output, EventEmitter, ViewEncapsulation } from '@angular/core'
import { ReviewPlan, ReviewerCandidateDto, UserReviewClient, SetDeferredRequest, AdminAuditEntry } from 'src/app/shared/services/ppv.api'
import { forkJoin, Observable, of, take } from 'rxjs'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

type SortColumn = 'revieweeDisplayName' | 'practiceName' | 'jobTitleCode' | 'reviewType' | 'cycle' | 'primaryReviewer' | 'reviewStatus' | 'isDeferred'
type PanelTab = 'overview' | 'reviewers' | 'cycle' | 'deferral' | 'history'

interface UserHistoryEntry {
   timestamp: Date | string
   kind: 'reviewer' | 'cycle' | 'deferral'
   label: string
   badgeClass: string
   title: string
   who: string
   scope: string
   reason: string
}

@Component({
   selector: 'app-user-management',
   templateUrl: './user-management.component.html',
   styleUrls: ['./user-management.component.scss'],
   standalone: false,
   encapsulation: ViewEncapsulation.None,
})
export class UserManagementComponent implements OnInit, OnChanges {
   @Input() allUsers: ReviewPlan[] = []
   @Input() usersLoading = false
   @Input() currentAdminName = ''

   @Output() usersChanged = new EventEmitter<ReviewPlan[]>()
   @Output() reloadUsers = new EventEmitter<void>()

   @Input() pendingUserSelect: any = null
   @Input() pendingDeferralId: number | null = null
   @Output() panelClosed = new EventEmitter<void>()
   @Output() panelOpened = new EventEmitter<boolean>()

   makeChangeForHospital = false
   undoChangeForHospital = false
   makeCycleChangeForHospital = false
   addingAddtionalHM = false

   activeSubTab: 'users' | 'deferrals' = 'users'
   deferralBadgeCount = 0

   filteredUsers: ReviewPlan[] = []
   userSearch = ''
   userJobCodeFilter = ''
   userCycleFilter = ''
   userHospitalFilter = ''

   /*hospitalOptions: string[] = []
   jobCodeOptions: string[] = []*/
   @Input() hospitalOptions: string[] = []
   @Input() jobCodeOptions: string[] = []

   private pendingFetched = false

   assignedCycleOpen = true
   deferUserOpen = true

   panelTab: PanelTab = 'overview'
   readonly panelTabs: Array<{ id: PanelTab; label: string }> = [
      { id: 'overview', label: 'Overview' },
      { id: 'reviewers', label: 'Reviewers' },
      { id: 'cycle', label: 'Cycle' },
      { id: 'deferral', label: 'Deferral' },
      { id: 'history', label: 'History' },
   ]

   userHistory: UserHistoryEntry[] = []
   recentHistory: UserHistoryEntry[] = []
   reviewerHistory: UserHistoryEntry[] = []
   cycleHistory: UserHistoryEntry[] = []
   deferralHistory: UserHistoryEntry[] = []
   userHistoryLoading = false

   currentReview: any = null
   pastReviews: any[] = []
   reviewSteps: Array<{ label: string; sub: string; done: boolean }> = []
   currentReviewStatus: { label: string; badge: string } = { label: 'No review yet', badge: 'badge-reenabled' }
   reviewProgressLoading = false
   primaryReviewerForCount = 0

   private allDeferralRequests: any[] = []
   private deferralRequestsLoaded = false

   // Badge for each review status. Used by the table's Current Review Progress column and the side panel.
   readonly reviewStatusDisplay: Record<string, { label: string; badge: string; order: number }> = {
      none: { label: 'No review yet', badge: 'badge-reenabled', order: 0 },
      'not-started': { label: 'Not started', badge: 'badge-reenabled', order: 1 },
      'in-progress': { label: 'In progress', badge: 'badge-progress', order: 2 },
      'in-review': { label: 'In review', badge: 'badge-review', order: 3 },
      completed: { label: 'Completed', badge: 'badge-completed', order: 4 },
   }

   public userDeferredOptions: Array<{ label: string; value: string | null }> = [
      { label: 'All statuses', value: '' },
      { label: 'Deferred', value: 'deferred' },
      { label: 'Active', value: 'active' },
   ]

   public userCycleOptions: Array<{ label: string; value: string | null }> = [
      { label: 'All cycles', value: '' },
      { label: 'aDVM Q1', value: 'aDVM Q1' },
      { label: 'aDVM Q2', value: 'aDVM Q2' },
      { label: 'aDVM Q3', value: 'aDVM Q3' },
      { label: 'aDVM Q4', value: 'aDVM Q4' },
      { label: 'HM', value: 'HM' },
      { label: 'COS', value: 'COS' },
      { label: 'RM', value: 'RM' },
      { label: 'DO', value: 'DO' },
      { label: 'OPEN', value: 'OPEN' },
      { label: 'TEAM', value: 'TEAM' },
      /*{ label: 'Clinical', value: 'CLINICAL' },
      { label: 'Cross-Trained', value: 'CROSSTRAINED' },
      { label: 'Reception', value: 'RECEPTION' },*/
   ]

   sortColumn: SortColumn = 'revieweeDisplayName'
   sortDir: 'asc' | 'desc' = 'asc'

   selectedUser: ReviewPlan | null = null

   reviewerSuccess: string | null = null
   reviewerError: string | null = null
   reviewerSaving = false

   rcReasonInput = ''
   editingReviewerRole: string | null = null
   editingReviewerUserId: number | null = null
   changeIsPrimaryReviewer = false
   reviewerCandidates: ReviewerCandidateDto[] = []
   pendingReviewerChanges: {
      role: string
      reviewerUserId: string
      oldName: string
      displayName: string
      rcReasonInput: string
   }[] = []

   cosOverrideName = ''
   cosaOverrideName = ''
   hmOverrideName = ''
   rmOverrideName = ''

   cycleChangeReason = ''
   editingDvmCycle = ''
   editingReviewType = ''
   dvmCycleSaving = false
   dvmCycleSuccess: string | null = null
   dvmCycleError: string | null = null

   deferReasonInput = ''
   deferSaving = false
   deferSuccess: string | null = null
   deferError: string | null = null
   userDeferredFilter = ''

   deferReasonCategory = ''
   deferQuarter: string | null = null

   readonly deferQuarterOptions: Array<{ value: string; label: string; sub: string; endsOn: string }> = [
      { value: 'Q4 2026', label: 'Q4 2026', sub: '(Oct – Dec)', endsOn: '2026-12-31' },
      { value: 'Q1 2027', label: 'Q1 2027', sub: '(Jan – Mar)', endsOn: '2027-03-31' },
      { value: 'Q2 2027', label: 'Q2 2027', sub: '(Apr – Jun)', endsOn: '2027-06-30' },
      { value: 'Q3 2027', label: 'Q3 2027', sub: '(Jul – Sep)', endsOn: '2027-09-30' },
      { value: '1 Year', label: '1 Year', sub: '', endsOn: '' },
   ]

   get isAutoApprovedDeferral(): boolean {
      return !!this.deferQuarter
   }

   selectDeferQuarter(value: string | null): void {
      this.deferQuarter = value
   }

   private readonly AVATAR_PALETTES = [
      { bg: '#e0eef6', fg: '#0374b6' },
      { bg: '#daf3f2', fg: '#26b5ae' },
      { bg: '#ffefcc', fg: '#976a12' },
      { bg: '#fdebe4', fg: '#f05d44' },
      { bg: '#e9dee9', fg: '#831183' },
      { bg: '#fae8e8', fg: '#d80c47' },
   ]

   constructor(private userReviewClient: UserReviewClient) {}

   ngOnInit(): void {
      //this.rebuildFilterOptions()
      this.filteredUsers = [...this.allUsers]
      this.applySort()
      this.loadDeferralBadgeCount()

      if (this.pendingDeferralId) {
         this.activeSubTab = 'deferrals'
      }
   }

   /** Load pending deferral count eagerly so the badge shows on first tab open */
   private loadDeferralBadgeCount(): void {
      this.userReviewClient.getDeferralRequests().subscribe({
         next: (data) => {
            this.allDeferralRequests = data ?? []
            this.deferralRequestsLoaded = true
            this.deferralBadgeCount = this.allDeferralRequests.filter((r) => r.status === 'Pending').length
         },
         error: () => {
            /* badge stays 0 on error, non-critical */
         },
      })
   }

   ngOnChanges(changes: SimpleChanges): void {
      if (changes['allUsers']) {
         //this.rebuildFilterOptions()
         this.onUserSearch()
         if (this.pendingUserSelect) {
            this.openPendingUser()
         }
      }
      if (changes['pendingUserSelect'] && this.pendingUserSelect) {
         //if (this.allUsers?.length > 0) {
         this.pendingFetched = false
         this.openPendingUser()
         //}
      }

      if (changes['pendingDeferralId'] && this.pendingDeferralId) {
         this.activeSubTab = 'deferrals'
      }
   }

   private openPendingUser(): void {
      this.activeSubTab = 'users'

      if (this.pendingUserSelect.practiceName) {
         this.userHospitalFilter = this.pendingUserSelect.practiceName
         this.onUserSearch()
      }

      const pendingId = String(this.pendingUserSelect.revieweeUserId ?? this.pendingUserSelect.revieweeId)
      const match = this.allUsers.find((u) => String(u.revieweeUserId) === pendingId)

      if (match) {
         this.selectUser(match)
         return
      }

      if (pendingId && pendingId !== 'undefined' && pendingId !== 'null' && !this.pendingFetched) {
         this.pendingFetched = true
         this.userReviewClient
            .getUser(+pendingId, '')
            .pipe(take(1))
            .subscribe({
               next: (u: any) => {
                  if (u) this.selectUser(u)
               },
               error: (err) => console.error('Could not fetch pending user:', err),
            })
      }
   }

   private getReviewTypeFromJobCode(jobTitleCode: string | null | undefined): string {
      switch (jobTitleCode) {
         case 'VET':
            return 'DVM'
         case 'HM':
            return 'HM'
         case 'AREA_MANAGER':
            return 'HM'
         case 'VET-COS':
            return 'COS'
         case 'OPS-RM':
            return 'RM'
         case 'CORP-DIR':
            return 'DO'
         default:
            return ''
      }
   }

   setSubTab(tab: 'users' | 'deferrals'): void {
      this.activeSubTab = tab

      if (tab === 'users') {
         this.closeUserEdit()
         this.userSearch = ''
         this.reloadUsers.emit()
      }
   }

   onDeferralCountChange(count: number): void {
      this.deferralBadgeCount = count
   }

   getUserCycleDisplay(u: any): string {
      if (['CLINICAL', 'CROSSTRAINED', 'RECEPTION'].includes(u.reviewType)) {
         return u.cycle === 'OPEN' ? 'OPEN' : 'TEAM'
      }

      if (['Q1', 'Q2', 'Q3', 'Q4'].includes(u.cycle)) {
         return `aDVM ${u.cycle}`
      } else if (u.cycle) {
         return `${u.cycle}`
      }

      switch (u.jobTitleCode) {
         case 'VET-COS':
            return 'COS'
         case 'HM':
            return 'HM'
         case 'OPS-RM':
         case 'AREA_MANAGER':
            return 'RM'
         case 'CORP-DIR':
            return 'DO'
         default:
            return ''
      }
   }

   /*
   private rebuildFilterOptions(): void {
      this.hospitalOptions = [...new Set(this.allUsers.map((u) => u.practiceName).filter(Boolean))].sort()
      this.jobCodeOptions = [...new Set(this.allUsers.map((u) => u.jobTitleCode).filter(Boolean))].sort()
   }
      */

   /*onUserSearch(): void {
      const search = this.userSearch.toLowerCase()
      this.filteredUsers = this.allUsers.filter((u) => {
         const matchesSearch = !search || u.revieweeDisplayName?.toLowerCase().includes(search) || u.practiceName?.toLowerCase().includes(search)
         const matchesRole = !this.userJobCodeFilter || u.jobTitleCode === this.userJobCodeFilter
         const matchesCycle = !this.userCycleFilter || this.getUserCycleDisplay(u) === this.userCycleFilter

         const matchesHospital = !this.userHospitalFilter || u.practiceName === this.userHospitalFilter
         const isDeferred = ['Approved', 'Deferred'].includes((u as any).deferralStatus ?? '')
         const matchesDeferred =
            !this.userDeferredFilter ||
            (this.userDeferredFilter === 'deferred' && isDeferred) ||
            (this.userDeferredFilter === 'active' && !isDeferred)
         return matchesSearch && matchesRole && matchesCycle && matchesHospital && matchesDeferred
      })
      this.applySort()
   }*/

   onUserSearch(): void {
      const search = this.userSearch.trim()

      if (this.usersLoading && search.length > 1) {
         this.userReviewClient
            .searchUsers(search)
            .pipe(take(1))
            .subscribe({
               next: (hits: any) => {
                  const known = new Set(this.allUsers.map((u) => String(u.revieweeUserId)))
                  const extra = (hits ?? []).filter((h: any) => !known.has(String(h.revieweeUserId)))
                  if (extra.length) this.allUsers = [...this.allUsers, ...extra]
                  this.filterLocally()
               },
               error: () => this.filterLocally(),
            })
         return
      }

      this.filterLocally()
   }

   private filterLocally(): void {
      const search = this.userSearch.toLowerCase()
      this.filteredUsers = this.allUsers.filter((u) => {
         const matchesSearch = !search || u.revieweeDisplayName?.toLowerCase().includes(search) || u.practiceName?.toLowerCase().includes(search)
         const matchesRole = !this.userJobCodeFilter || u.jobTitleCode === this.userJobCodeFilter
         const matchesCycle = !this.userCycleFilter || this.getUserCycleDisplay(u) === this.userCycleFilter

         const matchesHospital = !this.userHospitalFilter || u.practiceName === this.userHospitalFilter
         const isDeferred = ['Approved', 'Deferred'].includes((u as any).deferralStatus ?? '')
         const matchesDeferred =
            !this.userDeferredFilter ||
            (this.userDeferredFilter === 'deferred' && isDeferred) ||
            (this.userDeferredFilter === 'active' && !isDeferred)
         return matchesSearch && matchesRole && matchesCycle && matchesHospital && matchesDeferred
      })
      this.applySort()
   }

   setUserSort(col: SortColumn): void {
      if (this.sortColumn === col) {
         this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc'
      } else {
         this.sortColumn = col
         this.sortDir = 'asc'
      }
      this.applySort()
   }

   private applySort(): void {
      this.filteredUsers = [...this.filteredUsers].sort((a, b) => {
         let aVal = '',
            bVal = ''
         switch (this.sortColumn) {
            case 'revieweeDisplayName':
               aVal = a.revieweeDisplayName || ''
               bVal = b.revieweeDisplayName || ''
               break
            case 'practiceName':
               aVal = a.practiceName || ''
               bVal = b.practiceName || ''
               break
            case 'jobTitleCode':
               aVal = a.jobTitleCode || ''
               bVal = b.jobTitleCode || ''
               break
            case 'reviewType':
               aVal = a.reviewType || ''
               bVal = b.reviewType || ''
               break
            case 'cycle':
               aVal = this.getUserCycleDisplay(a)
               bVal = this.getUserCycleDisplay(b)
               break
            case 'primaryReviewer':
               aVal = this.getPrimaryReviewer(a).join(' ')
               bVal = this.getPrimaryReviewer(b).join(' ')
               break
            case 'reviewStatus':
               aVal = String(this.getRowReviewStatus(a).order)
               bVal = String(this.getRowReviewStatus(b).order)
               break
            case 'isDeferred':
               aVal = this.isActivelyDeferred(a as any) ? 'a' : 'b'
               bVal = this.isActivelyDeferred(b as any) ? 'a' : 'b'
               break
         }
         const cmp = aVal.localeCompare(bVal)
         return this.sortDir === 'asc' ? cmp : -cmp
      })
   }

   selectUser(user: ReviewPlan): void {
      this.selectedUser = { ...user }
      this.editingDvmCycle = user.cycle || this.getUserCycleDisplay(user)
      this.editingReviewType = user.reviewType
      this.editingReviewerRole = null
      this.editingReviewerUserId = null
      this.pendingReviewerChanges = []
      this.reviewerSuccess = null
      this.reviewerError = null
      this.dvmCycleSuccess = null
      this.dvmCycleError = null
      this.deferReasonInput = ''
      this.rcReasonInput = ''
      this.cycleChangeReason = ''
      this.deferSuccess = null
      this.deferError = null
      this.deferQuarter = user.whenOpenCompletedBy
      this.deferReasonCategory = (this.isActivelyDeferred(user) ? user.deferReason : '').split(':')[0].trim()
      this.cosOverrideName = user.cosOverrideName
      this.cosaOverrideName = user.cosaOverrideName
      this.hmOverrideName = user.hmOverrideName
      this.rmOverrideName = user.rmOverrideName
      this.makeCycleChangeForHospital = false
      this.addingAddtionalHM = false

      this.currentReview = null
      this.pastReviews = []
      this.reviewProgressLoading = false
      this.primaryReviewerForCount = this.allUsers.filter((u) => this.getPrimaryReviewer(u).includes(user.revieweeDisplayName)).length
      this.buildReviewSteps()
      this.setUserHistory([])
      this.loadUserHistory()
      this.loadReviewProgress()

      this.panelOpened.emit(true)

      console.log(this.selectedUser)
   }

   closeUserEdit(): void {
      this.selectedUser = null
      this.addingAddtionalHM = false
      this.pendingReviewerChanges = []
      this.panelTab = 'overview'
      this.panelClosed.emit()
   }

   getPrimaryReviewer(user: ReviewPlan): string[] {
      if (user?.jobTitleCode === 'VET') return [user.cosDisplayName || user.vetLeaderDisplayName].filter(Boolean)
      if (user?.jobTitleCode === 'HM') return [user.rmDisplayName].filter(Boolean)
      if (user?.jobTitleCode === 'VET-COS') return [user.vetLeaderDisplayName, user.rmDisplayName].filter(Boolean)
      if (user?.jobTitleCode === 'OPS-RM') return [user.directorDisplayName].filter(Boolean)
      if (user?.jobTitleCode === 'AREA_MANAGER') return [user.directorDisplayName].filter(Boolean)
      if (user?.jobTitleCode === 'CORP-DIR') return [user.vpDisplayName].filter(Boolean)
      if (user?.reviewType === 'CLINICAL' || user?.reviewType === 'CROSSTRAINED' || user?.reviewType === 'RECEPTION' || !user?.reviewType)
         return [user.hmDisplayName].filter(Boolean)
      return []
   }

   getPrimaryReviewerLabel(user: ReviewPlan): string {
      if (user?.jobTitleCode === 'VET') return user.cosDisplayName ? 'COS' : 'COSA'
      if (user?.jobTitleCode === 'HM') return 'RM'
      if (user?.jobTitleCode === 'VET-COS') return 'COSA / RM'
      if (user?.jobTitleCode === 'OPS-RM') return 'DO'
      if (user?.jobTitleCode === 'AREA_MANAGER') return 'DO'
      if (user?.jobTitleCode === 'CORP-DIR') return 'VP'
      if (user?.reviewType === 'CLINICAL' || user?.reviewType === 'CROSSTRAINED' || user?.reviewType === 'RECEPTION' || !user?.reviewType) return 'HM'
      return '—'
   }

   getPrimaryReviewerRole(user: ReviewPlan, index: number): string {
      const label = this.getPrimaryReviewerLabel(user)

      if (label === 'COSA / RM') {
         return index === 0 ? 'COSA' : 'RM'
      }

      return label
   }

   getOverrideName(role: string): string {
      switch (role) {
         case 'COS':
            return this.cosOverrideName
         case 'COSA':
            return this.cosaOverrideName
         case 'RM':
            return this.rmOverrideName
         case 'HM':
            return this.hmOverrideName
         default:
            return ''
      }
   }

   getReviewerRows(user: ReviewPlan): { role: string; name: string; overrideName: string }[] {
      if (!user) return []
      const primaryLabel = this.getPrimaryReviewerLabel(user)
      const rows: { role: string; name: string; overrideName: string }[] = []

      if (user.cosDisplayName !== user.revieweeDisplayName && primaryLabel !== 'COS') {
         rows.push({ role: 'COS', name: user.cosDisplayName, overrideName: this.cosOverrideName })
      }
      if (user.vetLeaderDisplayName !== user.revieweeDisplayName && !primaryLabel.includes('RM') && !primaryLabel.includes('COSA')) {
         rows.push({ role: 'COSA', name: user.vetLeaderDisplayName, overrideName: this.cosaOverrideName })
      }
      if (user.rmDisplayName !== user.revieweeDisplayName && !primaryLabel.includes('RM')) {
         rows.push({ role: 'RM', name: user.rmDisplayName, overrideName: this.rmOverrideName })
      }
      if (user.directorDisplayName !== user.revieweeDisplayName && primaryLabel !== 'DO') {
         rows.push({ role: 'DO', name: user.directorDisplayName, overrideName: '' })
      }
      if (user.vpDisplayName !== user.revieweeDisplayName && primaryLabel !== 'VP') {
         rows.push({ role: 'VP', name: user.vpDisplayName, overrideName: '' })
      }

      return rows
   }

   openReviewerModal(role: string, isPrimaryReviewer: boolean): void {
      this.changeIsPrimaryReviewer = isPrimaryReviewer
      console.log(this.changeIsPrimaryReviewer)
      this.editingReviewerRole = role
      this.editingReviewerUserId = null
      this.reviewerCandidates = []
      this.rcReasonInput = ''

      const roles = role === 'COSA' ? ['COSA', 'CORP-VP'] : [role]
      forkJoin(roles.map((r) => this.userReviewClient.getReviewerCandidates(r))).subscribe({
         next: (results) => {
            const combined = results.flat().filter(Boolean)
            // Deduplicate by id in case someone appears in both roles
            const seen = new Set<string>()
            this.reviewerCandidates = combined
               .filter((c) => !seen.has(c.id) && seen.add(c.id))
               .map((c) => ({ id: c.id, displayName: c.displayName, workEmail: c.workEmail }))
               .sort((a, b) => a.displayName.localeCompare(b.displayName))
         },
         error: () => {
            this.reviewerError = 'Failed to load reviewer options. Please try again.'
            setTimeout(() => (this.reviewerError = null), 3000)
         },
      })
   }

   confirmReviewerChange(): void {
      if (!this.editingReviewerRole || !this.editingReviewerUserId || !this.selectedUser) return

      const candidate = this.reviewerCandidates.find((c) => c.id === this.editingReviewerUserId.toString())

      if (!candidate) return

      const role = this.editingReviewerRole
      const oldName = this.getReviewerDisplayName(role)

      this.pendingReviewerChanges = this.pendingReviewerChanges.filter((c) => c.role !== role)

      this.pendingReviewerChanges.push({
         role,
         reviewerUserId: candidate.id,
         oldName,
         displayName: candidate.displayName,
         rcReasonInput: this.rcReasonInput,
      })

      this.applyReviewerChangeLocally(role, candidate.id, candidate.displayName)
      this.saveReviewerChanges()

      const patch = (u: ReviewPlan) => {
         // If NOT making the change for the whole hospital, only update the selected user
         if (!this.makeChangeForHospital && u.revieweeUserId !== this.selectedUser!.revieweeUserId) {
            return u
         }

         switch (role) {
            case 'COS':
               this.cosOverrideName = oldName
               return {
                  ...u,
                  cosDisplayName: candidate.displayName,
               }

            case 'COSA':
               this.cosaOverrideName = oldName
               return {
                  ...u,
                  vetLeaderDisplayName: candidate.displayName,
               }

            case 'RM':
               this.rmOverrideName = oldName
               return {
                  ...u,
                  rmDisplayName: candidate.displayName,
               }

            case 'HM':
               this.hmOverrideName = this.addingAddtionalHM ? '' : oldName
               if (this.addingAddtionalHM) {
                  return {
                     ...u,
                     hmDisplayName2: candidate.displayName,
                  }
               } else {
                  return {
                     ...u,
                     hmDisplayName: candidate.displayName,
                  }
               }

            case 'DO':
               return {
                  ...u,
                  directorDisplayName: candidate.displayName,
               }

            case 'VP':
               return {
                  ...u,
                  vpDisplayName: candidate.displayName,
               }

            default:
               return u
         }
      }

      const updatedAll = this.allUsers.map(patch)
      this.usersChanged.emit(updatedAll)

      this.filteredUsers = this.filteredUsers.map(patch)

      this.editingReviewerRole = null
      this.editingReviewerUserId = null
   }

   private getReviewerDisplayName(role: string): string {
      if (!this.selectedUser) return '—'
      switch (role) {
         case 'COS':
            return this.selectedUser.cosDisplayName || '—'
         case 'COSA':
            return this.selectedUser.vetLeaderDisplayName || '—'
         case 'RM':
            return this.selectedUser.rmDisplayName || '—'
         case 'VP':
            return this.selectedUser.vpDisplayName || '—'
         case 'DO':
            return this.selectedUser.directorDisplayName || '—'
         case 'TEAM':
         case 'CLINICAL':
         case 'CROSSTRAINED':
         case 'RECEPTION':
         case '':
            return this.selectedUser.hmDisplayName || '—'
         case 'HM':
            return this.selectedUser.hmDisplayName || '—'
         default:
            return '—'
      }
   }

   private getReviewerId(user: ReviewPlan): string {
      switch (this.selectedUser.reviewType) {
         case 'COS':
            return this.selectedUser.vetLeaderUserId
         case 'RM':
            return this.selectedUser.directorUserId
         case 'HM':
            return this.selectedUser.rmUserId
         case 'DO':
            return this.selectedUser.vpUserId
         case 'TEAM':
         case 'CLINICAL':
         case 'CROSSTRAINED':
         case 'RECEPTION':
         case '':
            return this.selectedUser.hmUserId
         case 'DVM':
         default:
            return this.selectedUser.cosUserId
      }
   }

   isActivelyDeferred(user: ReviewPlan): boolean {
      return ['Approved', 'Deferred'].includes((user as any).deferralStatus ?? '')
   }

   private applyReviewerChangeLocally(role: string, userId: string, displayName: string): void {
      if (!this.selectedUser) return
      switch (role) {
         case 'COS':
            this.selectedUser.cosUserId = userId
            this.selectedUser.cosDisplayName = displayName
            break
         case 'COSA':
            this.selectedUser.vetLeaderUserId = userId
            this.selectedUser.vetLeaderDisplayName = displayName
            break
         case 'RM':
            this.selectedUser.rmUserId = userId
            this.selectedUser.rmDisplayName = displayName
            break
         case 'VP':
            this.selectedUser.vpUserId = userId
            this.selectedUser.vpDisplayName = displayName
            break
         case 'DO':
            this.selectedUser.directorUserId = userId
            this.selectedUser.directorDisplayName = displayName
            break
         case 'HM':
            if (this.addingAddtionalHM) {
               this.selectedUser.hmDisplayName2 = displayName
               break
            } else {
               this.selectedUser.hmUserId = userId
               this.selectedUser.hmDisplayName = displayName
               break
            }
      }
   }

   public removeAssignmentOverride(role: string, overrideName: string): void {
      this.reviewerSaving = true
      this.reviewerError = null

      this.userReviewClient
         .removeAssignmentOverride(role, this.selectedUser!.revieweeUserId, this.undoChangeForHospital, this.selectedUser!.practiceName, overrideName)
         .subscribe({
            next: () => {
               this.reviewerSaving = false
               this.applyReviewerChangeLocally(role, this.selectedUser!.revieweeUserId, overrideName)

               const patch = (u: ReviewPlan) => {
                  if (u.revieweeUserId !== this.selectedUser!.revieweeUserId) return u
                  switch (role) {
                     case 'COS':
                        this.cosOverrideName = ''
                        return { ...u, cosDisplayName: overrideName }
                     case 'COSA':
                        this.cosaOverrideName = ''
                        return { ...u, vetLeaderDisplayName: overrideName }
                     case 'RM':
                        this.rmOverrideName = ''
                        return { ...u, rmDisplayName: overrideName }
                     case 'HM':
                        this.hmOverrideName = ''
                        return { ...u, hmDisplayName: overrideName }
                     default:
                        return u
                  }
               }
               const updatedAll = this.allUsers.map(patch)
               this.usersChanged.emit(updatedAll)
               this.filteredUsers = this.filteredUsers.map(patch)

               this.reviewerSuccess = 'Reviewer updated successfully.'
               setTimeout(() => (this.reviewerSuccess = null), 3000)
               this.loadUserHistory()
            },
            error: () => {
               this.reviewerSaving = false
               this.reviewerError = 'Failed to save reviewer change. Please try again.'
               setTimeout(() => (this.reviewerError = null), 4000)
            },
         })
   }

   private saveReviewerChanges(): void {
      if (!this.selectedUser || !this.pendingReviewerChanges.length) return
      this.reviewerSaving = true
      this.reviewerSuccess = null
      this.reviewerError = null

      const reviewType = this.getReviewTypeFromJobCode(this.selectedUser.jobTitleCode)
         ? this.getReviewTypeFromJobCode(this.selectedUser.jobTitleCode)
         : this.selectedUser.reviewType

      const baseReviewObject: SetDeferredRequest = {
         revieweeId: this.selectedUser.revieweeUserId,
         reviewType,
         isDeferred: false,
         deferReason: '',
         practiceSKey: this.selectedUser.practiceSKey,
         practiceName: this.selectedUser.practiceName,
         revieweeName: this.selectedUser.revieweeDisplayName,
         cosName: this.selectedUser.cosDisplayName,
         rmName: this.selectedUser.rmDisplayName,
         hmName: this.selectedUser.hmDisplayName,
         hmName2: this.selectedUser.hmDisplayName2,
         vetLeaderName: this.selectedUser.vetLeaderDisplayName,
         directorName: this.selectedUser.directorDisplayName,
         vpName: this.selectedUser.vpDisplayName,
         hireDate: this.selectedUser.hireDate,
      }

      const doNext = (idx: number) => {
         if (idx >= this.pendingReviewerChanges.length) {
            this.reviewerSaving = false
            this.reviewerSuccess = 'Reviewer updated successfully.'
            this.pendingReviewerChanges = []
            setTimeout(() => (this.reviewerSuccess = null), 3000)
            this.loadUserHistory()
            return
         }
         const change = this.pendingReviewerChanges[idx]
         this.userReviewClient
            .updateReviewer(
               {
                  role: change.role,
                  reviewerUserId: change.reviewerUserId,
                  oldName: change.oldName,
                  displayName: change.displayName,
                  rcReasonInput: change.rcReasonInput,
                  reviewObject: baseReviewObject,
               },
               this.selectedUser!.revieweeUserId,
               this.changeIsPrimaryReviewer,
               this.makeChangeForHospital,
               this.addingAddtionalHM
            )
            .subscribe({
               next: () => doNext(idx + 1),
               error: () => {
                  this.reviewerSaving = false
                  this.reviewerError = 'Failed to save reviewer change. Please try again.'
                  setTimeout(() => (this.reviewerError = null), 4000)
               },
            })
      }
      doNext(0)
   }

   saveDvmCycle(): void {
      if (!this.selectedUser) return
      this.dvmCycleSaving = true
      this.dvmCycleSuccess = null
      this.dvmCycleError = null

      const computedReviewType = this.getReviewTypeFromJobCode(this.selectedUser.jobTitleCode) || this.editingDvmCycle

      const newReviewType = computedReviewType === 'OPEN' ? this.selectedUser.reviewType : computedReviewType

      this.userReviewClient
         .updateCycle(
            {
               revieweeUserId: this.selectedUser.revieweeUserId,
               reviewerUserId: this.getReviewerId(this.selectedUser),
               cycle: this.editingDvmCycle,
               practiceSKey: this.selectedUser.practiceSKey,
               practiceName: this.selectedUser.practiceName,
               revieweeName: this.selectedUser.revieweeDisplayName,
               cosName: this.selectedUser.cosDisplayName,
               rmName: this.selectedUser.rmDisplayName,
               hmName: this.selectedUser.hmDisplayName,
               vetLeaderName: this.selectedUser.vetLeaderDisplayName,
               directorName: this.selectedUser.directorDisplayName,
               vpName: this.selectedUser.vpDisplayName,
               reviewType: this.editingReviewType ? this.editingReviewType : newReviewType,
               hireDate: this.selectedUser.hireDate,
               whenOpenCompletedBy: this.deferQuarter,
               cycleChangeReason: this.cycleChangeReason,
            },
            false,
            this.makeCycleChangeForHospital
         )
         .subscribe({
            next: () => {
               this.selectedUser!.cycle = this.editingDvmCycle
               this.selectedUser!.reviewType = this.editingReviewType
               const patch = (u: ReviewPlan) =>
                  u.revieweeUserId === this.selectedUser!.revieweeUserId
                     ? { ...u, cycle: this.editingDvmCycle, reviewType: this.editingReviewType ? this.editingReviewType : this.editingDvmCycle }
                     : u
               this.usersChanged.emit(this.allUsers.map(patch))
               this.filteredUsers = this.filteredUsers.map(patch)
               this.dvmCycleSaving = false
               this.dvmCycleSuccess = 'Cycle updated successfully.'
               setTimeout(() => (this.dvmCycleSuccess = null), 3000)
               this.selectedUser!.whenOpenCompletedBy = this.deferQuarter
               this.buildReviewSteps()
               this.loadUserHistory()
            },
            error: () => {
               this.dvmCycleSaving = false
               this.dvmCycleError = 'Failed to update cycle. Please try again.'
               setTimeout(() => (this.dvmCycleError = null), 4000)
            },
         })
   }

   deferUser(): void {
      if (!this.selectedUser || !this.deferReasonInput?.trim()) return
      this.deferSaving = true
      this.deferSuccess = null
      this.deferError = null

      const fullReason =
         this.deferReasonCategory === 'Other' || !this.deferReasonCategory
            ? this.deferReasonInput
            : `${this.deferReasonCategory}${this.deferReasonInput ? ': ' + this.deferReasonInput : ''}`

      const theReviewType = this.getReviewTypeFromJobCode(this.selectedUser.jobTitleCode)
         ? this.getReviewTypeFromJobCode(this.selectedUser.jobTitleCode)
         : this.selectedUser.reviewType

      const request = {
         revieweeId: this.selectedUser.revieweeUserId,
         reviewerUserId: this.getReviewerId(this.selectedUser),
         reviewType: theReviewType,
         isDeferred: true,
         deferReason: fullReason,
         practiceSKey: this.selectedUser.practiceSKey,
         practiceName: this.selectedUser.practiceName,
         revieweeName: this.selectedUser.revieweeDisplayName,
         cosName: this.selectedUser.cosDisplayName,
         rmName: this.selectedUser.rmDisplayName,
         hmName: this.selectedUser.hmDisplayName,
         vetLeaderName: this.selectedUser.vetLeaderDisplayName,
         directorName: this.selectedUser.directorDisplayName,
         vpName: this.selectedUser.vpDisplayName,
         hireDate: this.selectedUser.hireDate,
         whenCompletedBy: 'Unsure',
         cycle:
            this.selectedUser.reviewType == 'CROSSTRAINED' ||
            this.selectedUser.reviewType == 'CLINICAL' ||
            this.selectedUser.reviewType == 'RECEPTION' ||
            !theReviewType
               ? ''
               : theReviewType === 'DVM'
                 ? `aDVM ${this.selectedUser.cycle}`
                 : theReviewType,
      }

      this.userReviewClient.requestDeferral(request).subscribe({
         next: () => {
            this.deferSaving = false
            this.selectedUser!.isDeferred = true
            this.selectedUser!.deferReason = fullReason
            this.selectedUser!.deferralStatus = 'Approved'
            this.deferReasonInput = ''
            this.deferReasonCategory = ''

            // Patch the table row so the Deferred column updates immediately
            const patch = (u: ReviewPlan) =>
               u.revieweeUserId === this.selectedUser!.revieweeUserId
                  ? { ...u, isDeferred: true, deferReason: fullReason, deferralStatus: 'Approved' }
                  : u
            this.usersChanged.emit(this.allUsers.map(patch))
            this.filteredUsers = this.filteredUsers.map(patch)

            this.deferSuccess = `${this.selectedUser!.revieweeDisplayName} has been deferred.`
            setTimeout(() => (this.deferSuccess = null), 3000)
            this.loadUserHistory(true)
         },
         error: () => {
            this.deferSaving = false
            this.deferError = 'Failed to defer user. Please try again.'
            setTimeout(() => (this.deferError = null), 4000)
         },
      })
   }

   reEnableUser(): void {
      if (!this.selectedUser) return
      this.deferSaving = true
      this.deferSuccess = null
      this.deferError = null

      this.userReviewClient.reEnableDeferredUser(this.selectedUser.revieweeUserId).subscribe({
         next: () => {
            this.deferSaving = false
            this.selectedUser!.isDeferred = false
            this.selectedUser!.deferReason = ''
            this.selectedUser!.deferralStatus = 'ReEnabled'
            this.deferReasonInput = ''
            this.deferReasonCategory = ''

            // Patch the table row so the Deferred column updates immediately
            const patch = (u: ReviewPlan) =>
               u.revieweeUserId === this.selectedUser!.revieweeUserId ? { ...u, isDeferred: false, deferReason: '', deferralStatus: 'ReEnabled' } : u
            this.usersChanged.emit(this.allUsers.map(patch))
            this.filteredUsers = this.filteredUsers.map(patch)

            this.deferSuccess = `${this.selectedUser!.revieweeDisplayName} has been re-enabled.`
            setTimeout(() => (this.deferSuccess = null), 3000)
            this.loadUserHistory(true)
         },
         error: () => {
            this.deferSaving = false
            this.deferError = 'Failed to re-enable user. Please try again.'
            setTimeout(() => (this.deferError = null), 4000)
         },
      })
   }

   setPanelTab(tab: PanelTab): void {
      this.panelTab = tab
   }

   loadUserHistory(refreshDeferrals = false): void {
      if (!this.selectedUser?.revieweeUserId) return

      const userId = String(this.selectedUser.revieweeUserId)
      const deferrals$: Observable<any[]> =
         refreshDeferrals || !this.deferralRequestsLoaded ? this.userReviewClient.getDeferralRequests() : of(this.allDeferralRequests)

      this.userHistoryLoading = true

      forkJoin({
         audit: this.userReviewClient.getAuditLog(500, userId, this.selectedUser.practiceName || undefined),
         deferrals: deferrals$,
      }).subscribe({
         next: ({ audit, deferrals }) => {
            this.allDeferralRequests = deferrals ?? []
            this.deferralRequestsLoaded = true
            this.deferralBadgeCount = this.allDeferralRequests.filter((r) => r.status === 'Pending').length

            // Ignore the response if the admin switched to another user while it was loading
            if (String(this.selectedUser?.revieweeUserId) !== userId) return

            const auditEntries = (audit ?? []).map((e) => this.mapAuditEntry(e)).filter((h): h is UserHistoryEntry => !!h)
            const deferralEntries = this.allDeferralRequests
               .filter((d) => String(d.revieweeId) === userId)
               .reduce((all: UserHistoryEntry[], d: any) => all.concat(this.mapDeferralRequest(d)), [] as UserHistoryEntry[])

            this.setUserHistory([...auditEntries, ...deferralEntries])
            this.userHistoryLoading = false
         },
         error: (err) => {
            console.error('Failed to load user history:', err)
            this.userHistoryLoading = false
         },
      })
   }

   private setUserHistory(entries: UserHistoryEntry[]): void {
      this.userHistory = [...entries].sort((a, b) => dayjs.utc(b.timestamp).valueOf() - dayjs.utc(a.timestamp).valueOf())
      this.recentHistory = this.userHistory.slice(0, 3)
      this.reviewerHistory = this.userHistory.filter((h) => h.kind === 'reviewer')
      this.cycleHistory = this.userHistory.filter((h) => h.kind === 'cycle')
      this.deferralHistory = this.userHistory.filter((h) => h.kind === 'deferral')
   }

   private mapAuditEntry(e: AdminAuditEntry): UserHistoryEntry | null {
      if (e.details?.includes('has been deferred')) return null

      // 'Hospital Cycle Applied' = this user was one of the reviews a hospital-wide cycle change actually updated
      const isCycle = ['Cycle Changed', 'Hospital Cycle Changed', 'Hospital Cycle Applied', 'Cycle Config Updated'].includes(e.action || '')
      const isDeferral = /deferr|re-enabled/i.test(e.action || '')
      const isHospital = ['Hospital Reviewer Changed', 'Hospital Cycle Changed', 'Hospital Cycle Applied'].includes(e.action || '')

      return {
         timestamp: e.timestamp ?? '',
         kind: isCycle ? 'cycle' : isDeferral ? 'deferral' : 'reviewer',
         label: isCycle ? 'Cycle' : isDeferral ? 'Deferral' : 'Reviewer',
         badgeClass: isCycle
            ? 'audit-cycle'
            : isDeferral
              ? 'deferred-reviewee'
              : e.action === 'Primary Reviewer Changed'
                ? 'audit-primary-reviewer'
                : 'audit-reviewer',
         title: e.details || e.action || '',
         who: e.adminName || 'System',
         scope:
            isCycle && this.selectedUser?.whenOpenCompletedBy
               ? '(Complete by ' + this.selectedUser?.whenOpenCompletedBy + ')'
               : isHospital
                 ? 'Entire hospital'
                 : 'This user only',
         reason: e.reason || '',
      }
   }

   private mapDeferralRequest(d: any): UserHistoryEntry[] {
      const base = { kind: 'deferral' as const, label: 'Deferral', badgeClass: 'deferred-reviewee', scope: '' }

      const requestedAt = dayjs.utc(d.requestedOn)
      const resolvedAt = d.resolvedOn ? dayjs.utc(d.resolvedOn) : null
      const autoApproved = d.status === 'Approved' && !!resolvedAt && Math.abs(resolvedAt.diff(requestedAt, 'second')) < 60

      const entries: UserHistoryEntry[] = [
         {
            ...base,
            timestamp: d.requestedOn,
            title: autoApproved ? 'Deferred' : d.status === 'Pending' ? 'Deferral requested · pending review' : 'Deferral requested',
            who: d.requestedByName || '—',
            reason: d.reason || '',
         },
      ]

      if (resolvedAt && !autoApproved) {
         const titles: Record<string, string> = { Approved: 'Deferral approved', Rejected: 'Deferral rejected', ReEnabled: 'Re-enabled' }
         entries.push({
            ...base,
            timestamp: d.resolvedOn,
            title: titles[d.status] ?? `Deferral ${String(d.status || '').toLowerCase()}`,
            who: d.resolvedByName || '—',
            reason: d.rejectReason || '',
         })
      }

      return entries
   }

   private loadReviewProgress(): void {
      if (!this.selectedUser?.revieweeUserId) return

      const userId = String(this.selectedUser.revieweeUserId)
      const reviewType = this.getReviewTypeFromJobCode(this.selectedUser.jobTitleCode) || this.selectedUser.reviewType
      if (!reviewType) return

      this.reviewProgressLoading = true
      this.userReviewClient.getUserReviewsForReviewee(+userId, reviewType).subscribe({
         next: (reviews: any[]) => {
            if (String(this.selectedUser?.revieweeUserId) !== userId) return

            const list = reviews ?? []
            this.currentReview = list.find((r) => r.isCurrent) ?? list[0] ?? null
            this.pastReviews = list.filter((r) => r !== this.currentReview && r.isCompleted)
            this.buildReviewSteps()
            this.reviewProgressLoading = false
         },
         error: (err) => {
            console.error('Failed to load review progress:', err)
            this.reviewProgressLoading = false
         },
      })
   }

   private buildReviewSteps(): void {
      const u = this.selectedUser
      const r = this.currentReview

      if (!u) {
         this.reviewSteps = []
         return
      }

      const cycle = this.getUserCycleDisplay(u) || 'TEAM'
      const reviewerLabel = this.getPrimaryReviewerLabel(u) === '—' ? 'Reviewer' : this.getPrimaryReviewerLabel(u)

      this.reviewSteps = [
         { label: 'Review type set', sub: u.reviewType || 'Not set yet', done: !!u.reviewType },
         {
            label: `Assigned to ${cycle} cycle`,
            sub: cycle === 'OPEN' && u.whenOpenCompletedBy ? `Complete by ${u.whenOpenCompletedBy}` : '',
            done: true,
         },
         { label: 'Self-assessment submitted', sub: this.formatShortDate(r?.dvmSubmittedOn) || 'Not submitted', done: !!r?.dvmSubmittedOn },
         {
            label: `${reviewerLabel} performance review submitted`,
            sub: this.formatShortDate(r?.cosSubmittedOn) || 'Not submitted',
            done: !!r?.cosSubmittedOn,
         },
         { label: 'Review completed', sub: this.formatShortDate(r?.completedOn) || 'Not completed', done: !!r?.isCompleted },
      ]

      // hasStarted comes from the table row, because this review endpoint doesn't return it
      this.currentReviewStatus =
         this.reviewStatusDisplay[this.getReviewStatus(r ? { ...r, hasStarted: r.hasStarted || (u as any).hasStarted } : null)]
   }

   /** Same rules as the Team Member Reviews page. Pass null when the user has no review record. */
   getReviewStatus(r: any): string {
      if (!r) return 'none'
      if (r.isCompleted) return 'completed'
      if (r.dvmSubmittedOn && r.cosSubmittedOn) return 'in-review'
      if (!r.dvmSubmittedOn && !r.cosSubmittedOn && !r.hasStarted) return 'not-started'
      return 'in-progress'
   }

   getRowReviewStatus(u: any): { label: string; badge: string; order: number } {
      return this.reviewStatusDisplay[this.getReviewStatus(u?.hasReview ? u : null)]
   }

   getEmploymentStatus(user: ReviewPlan): string {
      const status = (user?.revieweeUserStatus || '').toLowerCase()
      if (!status) return '—'
      if (status.includes('loa')) return 'On leave'
      return status.charAt(0).toUpperCase() + status.slice(1)
   }

   formatHireDate(date: Date | string | null | undefined): string {
      return date ? dayjs(date).format('MM/DD/YYYY') : '—'
   }

   formatShortDate(date: Date | string | null | undefined): string {
      return date ? dayjs.utc(date).local().format('MM/DD/YYYY') : ''
   }

   formatHistoryDay(date: Date | string): string {
      return dayjs.utc(date).local().format('MMM DD, YYYY')
   }

   formatHistoryTime(date: Date | string): string {
      return dayjs.utc(date).local().format('h:mm A')
   }

   getInitials(name: string): string {
      if (!name) return '?'
      const parts = name.trim().split(/\s+/)
      return parts.length >= 2 ? (parts[0][0] + parts[parts.length - 1][0]).toUpperCase() : name.substring(0, 2).toUpperCase()
   }

   getAvatarBg(name: string): string {
      return this.AVATAR_PALETTES[this.hashName(name) % this.AVATAR_PALETTES.length].bg
   }

   getAvatarColor(name: string): string {
      return this.AVATAR_PALETTES[this.hashName(name) % this.AVATAR_PALETTES.length].fg
   }

   private hashName(name: string): number {
      if (!name) return 0
      return name.split('').reduce((acc, c) => acc + c.charCodeAt(0), 0)
   }
}
